import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (v) => `Rp ${(v || 0).toLocaleString('id-ID')}`;

const BookSection = ({ book, projects }) => {
  const isCompany = book === 'company';
  const total = projects.reduce((s, p) => s + (p.amount || 0), 0);
  return (
    <div
      className={`border-l-4 pl-3 py-2 ${isCompany ? 'border-red-500 bg-red-50/40' : 'border-blue-500 bg-blue-50/40'}`}
      data-testid={`mixed-book-section-${book}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wide text-white ${
              isCompany ? 'bg-red-500' : 'bg-blue-600'
            }`}
          >
            {book}
          </span>
          <span className="text-sm text-slate-600">
            {projects.length} project{projects.length > 1 ? 's' : ''}
          </span>
        </div>
        <span className="font-bold text-slate-800">{formatCurrency(total)}</span>
      </div>
      <div className="space-y-1 text-sm">
        {projects.map((p, i) => (
          <div key={i} className="flex justify-between text-slate-700 pl-1">
            <span>
              {p.projectName}{' '}
              <span className="text-xs text-slate-500">({p.paymentPercent}%)</span>
            </span>
            <span className="tabular-nums">{formatCurrency(p.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * MixedExecuteDialog
 * ------------------
 * Renders a 2-section read-only preview of a multi-book planner (Freelance +
 * Company) with shared editable fields (date, description, proof file). On
 * submit, calls `/api/postponed-payments/{id}/execute-multi` which creates
 * one transaction per book atomically.
 *
 * Props:
 *  - planner: { id, teamMemberName, projects: [...], totalAmount }
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - onSuccess: () => void   -- called after backend confirms execution
 */
const MixedExecuteDialog = ({ planner, open, onOpenChange, onSuccess }) => {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (planner) {
      setDescription(`Payment - ${planner.teamMemberName || ''}`);
    }
  }, [planner]);

  if (!planner) return null;

  // Group projects by finance book
  const groups = (planner.projects || []).reduce((acc, p) => {
    const b = p.financeBook || 'freelance';
    (acc[b] = acc[b] || []).push(p);
    return acc;
  }, {});

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Convert to base64 (matches existing pattern in FinanceDetail)
    const reader = new FileReader();
    reader.onloadend = () => setProofFile(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('cse_token');
      const res = await axios.post(
        `${API}/postponed-payments/${planner.id}/execute-multi`,
        { date, description, proofFile },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = res.data.count || 0;
      toast.success(`Created ${n} transactions across ${n} book(s). Planner executed.`);
      window.dispatchEvent(new CustomEvent('app:trash-updated'));
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to execute');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="mixed-execute-dialog">
        <DialogHeader>
          <DialogTitle>Execute Multi-Book Payment — {planner.teamMemberName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            This planner pays projects from {Object.keys(groups).length} different books.
            Submitting will create one transaction per book with the shared fields below.
            Each project&#39;s amount and book below are locked (from the planner).
          </p>

          {/* Two read-only book sections */}
          <div className="space-y-3">
            {groups.freelance && groups.freelance.length > 0 && (
              <BookSection book="freelance" projects={groups.freelance} />
            )}
            {groups.freelance && groups.company && (
              <div className="border-t border-dashed border-slate-300 my-2" />
            )}
            {groups.company && groups.company.length > 0 && (
              <BookSection book="company" projects={groups.company} />
            )}
          </div>

          {/* Shared editable fields */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <div>
              <Label htmlFor="mixed-date">Tanggal (shared)</Label>
              <Input
                id="mixed-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="mixed-date-input"
              />
            </div>
            <div>
              <Label htmlFor="mixed-desc">Description (shared)</Label>
              <Input
                id="mixed-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="mixed-desc-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="mixed-proof">Proof file (shared, optional)</Label>
            <Input
              id="mixed-proof"
              type="file"
              accept="image/*,application/pdf"
              onChange={handleProofUpload}
              data-testid="mixed-proof-input"
            />
            {proofFile && <p className="text-xs text-green-700 mt-1">✓ Proof attached</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="mixed-execute-submit"
          >
            {busy ? 'Creating...' : `Create ${Object.keys(groups).length} Transactions`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MixedExecuteDialog;
